import { REPORT, REPORT_RANGE } from '@consts/reports';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, countUnique } from '@utils/summaries';
import { Materials } from '@consts/materials';
import { basicSort, dateSort } from '@utils/sort';
import { ReportTableChart } from '../../ReportTableChart';

export const OverallMaterialTypeCutPct = () => {
    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.PRODUCTS_OVERALL_MATERIAL_TYPE_CUT_PERCENTAGE,
        params: {},
    });

    const item = data[0];

    return (
        <BaseReport
            title={'Material Type % Rate'}
            description={'This chart shows the % breakdown of each material type for all of your cuts, over all time. '}
            note={'Note: Fusion data includes both Fusion and Fusion for Curved Devices.'}
            rangeText={'All Time'}
            loading={loading}
            hasData={hasData}
            splitReportElement={
                <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
                    <ReportTableChart
                        title={'Global Averages'}
                        rows={[
                            [Materials.Original, `${item?.globalOriginalPct}%`],
                            [Materials.Matte, `${item?.globalMattePct}%`],
                            [Materials.Fusion, `${item?.fusionPct}%`],
                            [Materials.StyleSkins, `${item?.styleSkinPct}%`],
                            [Materials.Pruvia, `${item?.pruviaPct}%`],
                        ]}
                    />
                    <ReportTableChart
                        title={'Top Performers'}
                        rows={[
                            [Materials.Original, `${item?.globalOriginalMaxPct}%`],
                            [Materials.Matte, `${item?.globalMatteMaxPct}%`],
                            [Materials.Fusion, `${item?.globalFusionMaxPct}%`],
                            [Materials.StyleSkins, `${item?.globalStyleSkinMaxPct}%`],
                            [Materials.Pruvia, `${item?.globalPruviaMaxPct}%`],
                        ]}
                    />
                </div>
            }
            tableOptions={{
                headers: ['Material', 'Material %', 'Global Avg', 'Global Max'],
                data: data.flatMap((item) => [
                    [Materials.Original, item.originalPct, item.globalOriginalPct, item.globalOriginalMaxPct],
                    [Materials.Matte, item.mattePct, item.globalMattePct, item.globalMatteMaxPct],
                    [Materials.Fusion, item.fusionPct, item.globalFusionPct, item.globalFusionMaxPct],
                    [Materials.StyleSkins, item.styleSkinPct, item.globalStyleSkinPct, item.globalStyleSkinMaxPct],
                    [Materials.Pruvia, item.pruviaPct, item.globalPruviaPct, item.globalPruviaMaxPct],
                ]),
                summaries: [countUnique, countUnique, average, average],
                sort: [dateSort(REPORT_RANGE.MONTHLY), basicSort, basicSort, basicSort],
            }}
            chartOptions={{
                title: {
                    text: '',
                },
                xAxis: {
                    categories: Object.values(Materials),
                },
                chart: {
                    type: 'pie',
                    animation: false,
                    height: 300,
                },
                legend: {
                    layout: 'vertical',
                    align: 'center',
                    verticalAlign: 'middle',
                    padding: 20,
                    x: 120,
                    maxHeight: 200,
                    labelFormat: '{name}: [{y}]%',
                },
                credits: { enabled: false },
                tooltip: {
                    headerFormat: '',
                    pointFormat: '{point.name}: {point.y}%',
                },
                plotOptions: {
                    pie: {
                        center: ['40%', '50%'],
                        allowPointSelect: false,
                        cursor: 'pointer',
                        dataLabels: {
                            enabled: false,
                        },
                        showInLegend: true,
                    },
                    series: {
                        animation: {
                            duration: 0,
                        },
                    },
                },
                series: [
                    {
                        type: 'pie',
                        name: 'Your Material Type %',
                        data: data.flatMap((item) => [
                            { name: Materials.Original, y: item.originalPct },
                            { name: Materials.Matte, y: item.mattePct },
                            { name: Materials.Fusion, y: item.fusionPct },
                            { name: Materials.StyleSkins, y: item.styleSkinPct },
                            { name: Materials.Pruvia, y: item.pruviaPct },
                        ]),
                    },
                ],
                responsive: {
                    rules: [
                        {
                            condition: {
                                maxWidth: 600,
                            },
                            chartOptions: {
                                legend: {
                                    x: 0,
                                    verticalAlign: 'bottom',
                                    align: 'center',
                                    maxHeight: 85,
                                    padding: 0,
                                    margin: 0,
                                },
                                plotOptions: {
                                    pie: {
                                        center: ['50%', '50%'],
                                        size: 180,
                                    },
                                },
                            },
                        },
                        {
                            condition: {
                                maxWidth: 960,
                                minWidth: 601,
                            },
                            chartOptions: {
                                legend: {
                                    x: 130,
                                },
                                plotOptions: {
                                    pie: {
                                        center: ['38%', '45%'],
                                        size: 200,
                                    },
                                },
                            },
                        },
                    ],
                },
            }}
        />
    );
};
